import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheck, FaPlus, FaMinus, FaChartLine, FaShieldAlt, FaCoins, FaUserCheck, FaNetworkWired, FaFileContract } from 'react-icons/fa'

const CryptoFintechs = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index)
  }

  const services = [
    {
      icon: <FaCoins className="text-4xl text-accent mb-4" />,
      title: 'Stablecoin Reserve Audits',
      description: 'Independent verification of reserves backing your stablecoin, ensuring 1:1 backing and transparent reporting to users.'
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent mb-4" />,
      title: 'SOC 2 Type II Examinations',
      description: 'Demonstrate security and operational maturity to enterprise customers and institutional partners.'
    },
    {
      icon: <FaChartLine className="text-4xl text-accent mb-4" />,
      title: 'Financial Statement Audits',
      description: 'GAAP-compliant audits conducted by crypto-native CPAs who understand your unique business model.'
    },
    {
      icon: <FaUserCheck className="text-4xl text-accent mb-4" />,
      title: 'Regulatory Compliance Support',
      description: 'Navigate evolving FinTech regulations with expert guidance on compliance requirements.'
    },
    {
      icon: <FaNetworkWired className="text-4xl text-accent mb-4" />,
      title: 'Smart Contract Audits',
      description: 'Security reviews of smart contracts powering your FinTech products and services.'
    },
    {
      icon: <FaFileContract className="text-4xl text-accent mb-4" />,
      title: 'AML/KYC Program Review',
      description: 'Assessment of your anti-money laundering and know-your-customer compliance programs.'
    },
  ]

  const challengeItems = [
    {
      title: 'Building Trust with Traditional Financial Institutions',
      content: 'Crypto-native FinTech companies often struggle to gain acceptance from traditional banks and financial institutions. Professional audits and attestations provide the credibility needed to establish banking relationships and institutional partnerships.'
    },
    {
      title: 'Meeting Regulatory Requirements Across Jurisdictions',
      content: 'Operating in the intersection of crypto and traditional finance means navigating complex regulatory landscapes. Our team helps you understand and meet requirements from FinCEN, state regulators, and international authorities.'
    },
    {
      title: 'Securing Institutional Customers and Partners',
      content: 'Enterprise customers and institutional partners require evidence of financial stability, security controls, and regulatory compliance. Independent audits demonstrate your commitment to professional standards.'
    },
    {
      title: 'Accessing Capital and Investment',
      content: 'Investors increasingly demand professional financial reporting and independent assurance before committing capital. Clean audit opinions and control reports facilitate fundraising and valuation.'
    },
  ]

  return (
    <>
      <Helmet>
        <title>Crypto & Stablecoin FinTech Audit Services - Proof of Reserves Auditor</title>
        <meta name="description" content="Specialized audit and assurance services for cryptocurrency FinTech companies and stablecoin issuers." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 overflow-hidden">
          {/* Animated Particle Wave Background */}
          <div className="absolute inset-0 opacity-30">
            <svg className="absolute bottom-0 w-full h-auto" viewBox="0 0 1280 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4A9BFF" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#4A9BFF" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#4A9BFF" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              {/* Multiple wave layers for depth */}
              <path
                d="M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.4"
              >
                <animate
                  attributeName="d"
                  dur="8s"
                  repeatCount="indefinite"
                  values="
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,250 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                />
              </path>

              <path
                d="M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.3"
              >
                <animate
                  attributeName="d"
                  dur="10s"
                  repeatCount="indefinite"
                  values="
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,290 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                />
              </path>

              {/* Dotted particle effect */}
              <g opacity="0.6">
                {[...Array(50)].map((_, i) => {
                  const x = (i * 25.6) % 1280;
                  const y = 150 + (i * 13) % 200;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="2"
                      fill="#4A9BFF"
                      opacity="0.5"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.3;0.8;0.3"
                        dur={`${3 + (i % 4)}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="cy"
                        values={`${y};${y - 20};${y}`}
                        dur={`${4 + (i % 3)}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  )
                })}
              </g>
            </svg>
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Crypto & Stablecoin FinTech
                <br />
                <span className="text-accent">Audit Services</span>
              </h1>
              <p className="text-2xl text-gray-200 mb-8">
                Bridge traditional finance and crypto innovation with confidence
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Gain Trust from Traditional Financial Institutions
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Navigate Complex Multi-Jurisdictional Regulations
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Secure Enterprise Customers and Institutional Partners
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>

        {/* The Challenge */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-bold text-primary mb-6">
                The Crypto FinTech Challenge
              </h2>
              <p className="text-xl text-gray-600">
                Operating at the intersection of cryptocurrency and traditional finance presents unique challenges requiring specialized expertise and credibility.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">🏦</div>
                <h3 className="text-xl font-bold text-primary mb-3">Banking Access</h3>
                <p className="text-gray-600">
                  Establishing and maintaining banking relationships requires demonstrating robust financial controls and regulatory compliance.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">⚖️</div>
                <h3 className="text-xl font-bold text-primary mb-3">Regulatory Uncertainty</h3>
                <p className="text-gray-600">
                  Evolving regulations across jurisdictions create compliance challenges requiring expert navigation and proactive preparation.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">💼</div>
                <h3 className="text-xl font-bold text-primary mb-3">Institutional Trust</h3>
                <p className="text-gray-600">
                  Building credibility with traditional financial institutions and enterprise customers demands professional assurance and transparency.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Comprehensive Services for Crypto FinTech Companies
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-lg hover:shadow-xl transition-shadow">
                  {service.icon}
                  <h3 className="text-xl font-bold mb-3 text-primary">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                DISCUSS YOUR NEEDS
              </Link>
            </div>
          </div>
        </section>

        {/* Challenge Accordion */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Overcoming Crypto FinTech Challenges
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {challengeItems.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-4">{item.title}</span>
                    {openAccordion === index ? (
                      <FaMinus className="text-accent flex-shrink-0" />
                    ) : (
                      <FaPlus className="text-accent flex-shrink-0" />
                    )}
                  </button>
                  
                  {openAccordion === index && (
                    <div className="px-6 pb-6 text-gray-700">
                      <p>{item.content}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-primary mb-12">
              Common FinTech Use Cases
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-accent">Stablecoin Issuers</h3>
                <p className="text-gray-600 mb-4">
                  Independent attestation of reserve holdings ensuring transparency and building user confidence in your stablecoin's backing.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Monthly or real-time reserve verification</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Public attestation reports</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Regulatory compliance support</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-accent">Crypto Banking Services</h3>
                <p className="text-gray-600 mb-4">
                  Demonstrate financial stability and operational controls to regulators and traditional banking partners.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Financial statement audits</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>SOC 2 Type II reports</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>AML/KYC program review</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-accent">Crypto Payment Solutions</h3>
                <p className="text-gray-600 mb-4">
                  Win enterprise merchants and maintain banking relationships with professional audit reports.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Settlement verification</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Reserve adequacy attestations</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Transaction control assessments</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-accent">DeFi-Traditional Bridge Platforms</h3>
                <p className="text-gray-600 mb-4">
                  Validate your bridging mechanisms and demonstrate security to both crypto and traditional finance stakeholders.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Smart contract security audits</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Custody control examinations</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Cross-chain verification</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-primary mb-12">
              Why Crypto FinTech Leaders Choose Us
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">7+</div>
                <div className="text-gray-600">Years of Crypto FinTech Experience</div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">50+</div>
                <div className="text-gray-600">FinTech Companies Served</div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">$10B+</div>
                <div className="text-gray-600">In Verified Stablecoin Reserves</div>
              </div>
            </div>

            <div className="max-w-3xl mx-auto bg-gray-50 rounded-lg p-8 shadow-lg">
              <p className="text-sm text-gray-600 mb-2">Stablecoin Issuer CEO</p>
              <blockquote className="text-2xl font-bold text-primary">
                "Their deep understanding of both traditional finance and crypto enabled us to navigate regulatory requirements while maintaining our innovative edge."
              </blockquote>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent">
              Ready to Bridge Crypto and Traditional Finance?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Let's discuss how our specialized audit and assurance services can help you build credibility, secure partnerships, and scale your crypto FinTech business.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              SCHEDULE CONSULTATION
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default CryptoFintechs